'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { debounce } from 'underscore';
import { createOptimizedOmit, EMPTY_OBJECT } from 'DesignData/lib/object';
import { validateJSON } from 'DesignData/lib/code/errors';
import { updateEditorSession } from 'DesignData/actions/codemirror/Session';
import * as statusBarActions from 'DesignData/actions/StatusBar';
import { hydrate as refreshSettingsState } from 'DesignData/actions/codemirror/Options';
import { getCodemirrorEditorSessionBySessionId, makeGetCodemirrorHistory } from 'DesignData/selectors/codemirror';
import { getEslintConfigObject, getIsEnabled as getEnableEsLint } from 'DesignData/selectors/javaScriptLinter';
import { getUserId } from 'DesignData/selectors/auth';
import { SET_CURSOR_IN_IDE_CODE_MIRROR, CODEMIRROR_SESSION_REDO, CODEMIRROR_SESSION_UNDO } from 'DesignData/enums/FlashKeys';
import 'codemirror';
import 'react-codemirror/mode/hubl';
import 'codemirror-graphql/hint';
import 'codemirror-graphql/lint';
import 'codemirror-graphql/mode';
import { Pos } from 'codemirror';
import ReactCodeMirror from 'react-codemirror';
import { getAllFoldIndices } from 'react-codemirror/util/Folds';
import annotations from 'react-codemirror/decorator/annotations';
import foldGutter from 'react-codemirror/decorator/foldGutter';
import hublValidation from 'react-codemirror/decorator/hublValidation';
import find from 'react-codemirror/decorator/find';
import gutterClickEvents from 'react-codemirror/decorator/gutterClickEvents';
import highlightLines from 'react-codemirror/decorator/highlightLines';
import comment from 'react-codemirror/decorator/comment';
import userSettings from 'react-codemirror/decorator/userSettings';
import eslint from 'react-codemirror/decorator/eslint';
import Position from 'react-codemirror/enum/Position';
import { refresh } from 'react-codemirror/util/Editor';
import debouncedInput from 'DesignEditorsUI/components/shared/decorators/debouncedInput';
import CodemirrorManager from 'DesignEditorsUI/components/shared/statusBar/CodemirrorManager';
import { compose } from 'redux';
const SESSION_DEBOUNCE_PERIOD = 100;
const HISTORY_DEBOUNCE_PERIOD = 500;
const CURSOR_DEBOUNCE_PERIOD = 25;
/**
 * ReactCodeMirror with added features from decorators applied.
 * @class
 */

class WrappedCodeMirror extends Component {
  get className() {
    return classNames(this.props.className, this.props.readOnly && 'is--read-only');
  }

  render() {
    return /*#__PURE__*/_jsx(ReactCodeMirror, Object.assign({}, this.props, {
      className: this.className
    }));
  }

}

WrappedCodeMirror.propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool
};
const DecoratedCodeMirror = compose(debouncedInput(), userSettings(), hublValidation(), eslint(), foldGutter(), gutterClickEvents(), highlightLines(), annotations(), find(), comment())(WrappedCodeMirror);
/**
 * Fields to remove from props passed to react-codemirror.
 * @type {Array.<String>}
 */

const omitBlacklistedRenderProps = createOptimizedOmit(['sessionId', 'disableSession', 'updateEditorSession', 'refCodemirror', 'onCursorActivity', 'onScroll', 'onFold', 'onUnfold', 'setCursorFlash', 'triggerUndoFlash', 'triggerRedoFlash', 'codemirrorType', 'registerStatusBar', 'updateHistory'], true);
const updateCompareIgnoreProps = {
  // Update whenever these change.
  sessionId: true,
  userId: true,
  // Only update when these change from false to true
  triggerUndoFlash: true,
  triggerRedoFlash: true
};
/**
 * Creates a closure to restore codemirror session props.
 */

const getRestoreSession = props => codemirror => {
  const {
    disableSession,
    session,
    history
  } = props;

  if (history && history.done && history.undone) {
    codemirror.doc.setHistory(history);
  } else {
    codemirror.doc.clearHistory();
  }

  if (session && !disableSession) {
    codemirror.operation(() => {
      const {
        scrollInfo,
        selections,
        foldIndices
      } = session;

      if (scrollInfo) {
        try {
          codemirror.scrollTo(scrollInfo.left, scrollInfo.top);
        } catch (e) {
          console.error(`Codemirror scroll error: %o`, e);
        }
      }

      if (selections && selections.length) {
        try {
          codemirror.setSelections(selections, null, {
            scroll: false
          });
        } catch (e) {
          console.error(`Codemirror selections error: %o`, e);
        }
      }

      if (foldIndices && foldIndices.length) {
        try {
          // If line index for fold is greater than there are lines, codemirror's
          // fold addon does not guard against using an empty line and will throw.
          // https://sentry.hubteam.com/sentry/designmanagerui/issues/8879059/
          const lineCount = codemirror.lineCount();
          foldIndices.every(idx => {
            if (idx >= lineCount || codemirror.getLine(idx) == null) {
              return false;
            }

            codemirror.foldCode(idx);
            return true;
          });
        } catch (e) {
          console.error(`Codemirror folds error: %o`, e);
        }
      }
    });
  }
};

function makeMapStateToProps() {
  const getCodemirrorHistory = makeGetCodemirrorHistory();
  const restoreSessionClosureProps = {};
  const restoreSession = getRestoreSession(restoreSessionClosureProps);
  return function mapStateToProps(state, props) {
    const {
      sessionId
    } = props;
    const history = getCodemirrorHistory(state, props);
    const session = getCodemirrorEditorSessionBySessionId(state, props);
    Object.assign(restoreSessionClosureProps, props, {
      session,
      history
    });
    const cursorFlash = state.flash[SET_CURSOR_IN_IDE_CODE_MIRROR];
    const undoFlash = state.flash[CODEMIRROR_SESSION_UNDO];
    const redoFlash = state.flash[CODEMIRROR_SESSION_REDO];
    const triggerUndoFlash = undoFlash && undoFlash.sessionId === sessionId;
    const triggerRedoFlash = redoFlash && redoFlash.sessionId === sessionId;
    let setCursorFlash = null;

    if (cursorFlash && cursorFlash.sessionId === sessionId) {
      const {
        lineNumber,
        endLineNumber,
        column,
        endColumn
      } = cursorFlash;
      const from = new Pos(lineNumber, column);
      const to = endLineNumber != null && endColumn != null ? new Pos(endLineNumber, endColumn) : new Pos(lineNumber, column);
      setCursorFlash = {
        from,
        to
      };
    }

    const enableEsLint = !!(props.enableEsLint && getEnableEsLint(state));
    const esLintConfig = enableEsLint ? getEslintConfigObject(state) : EMPTY_OBJECT;
    return {
      userId: getUserId(state),
      setCursorFlash,
      triggerUndoFlash,
      triggerRedoFlash,
      restoreSession,
      enableEsLint,
      esLintConfig
    };
  };
}

const mapDispatchToProps = {
  updateEditorSession,
  refreshSettingsState,
  // Status bar
  registerStatusBar: statusBarActions.registerStatusBar
};
/**
 * @class IDE specific CodeMirror wrapper.
 */

class IDECodeMirror extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      isCodeMirrorMounted: false
    };
    this.__compareProps = [];
    this.saveScrollInfo = debounce(() => {
      if (!this.codemirror) return;
      this.updateEditorSession({
        scrollInfo: this.codemirror.getScrollInfo()
      });
    }, SESSION_DEBOUNCE_PERIOD);
    this.saveFoldInfo = debounce(() => {
      if (!this.codemirror) return;
      this.updateEditorSession({
        foldIndices: getAllFoldIndices(this.codemirror)
      });
    }, SESSION_DEBOUNCE_PERIOD);
    this.saveCursorInfo = debounce(() => {
      if (!this.codemirror) return;
      this.updateEditorSession({
        selections: this.codemirror.listSelections()
      });
    }, CURSOR_DEBOUNCE_PERIOD);
    this.saveHistoryInfo = debounce(() => {
      if (!this.codemirror) return;
      if (!this.props.updateHistory) return;
      const {
        doc
      } = this.codemirror;
      this.updateEditorSession({
        history: doc.getHistory(),
        historySize: doc.historySize()
      });
    }, HISTORY_DEBOUNCE_PERIOD);

    this.onCursorActivity = data => {
      this.saveCursorInfo();
      const {
        onCursorActivity
      } = this.props;

      if (onCursorActivity) {
        onCursorActivity(data);
      }
    };

    this.onScroll = data => {
      this.saveScrollInfo();
      const {
        onScroll
      } = this.props;

      if (onScroll) {
        onScroll(data);
      }
    };

    this.onFold = data => {
      this.saveFoldInfo();
      const {
        onFold
      } = this.props;

      if (onFold) {
        onFold(data);
      }
    };

    this.onUnfold = data => {
      this.saveFoldInfo();
      const {
        onUnfold
      } = this.props;

      if (onUnfold) {
        onUnfold(data);
      }
    };

    this.onChange = data => {
      this.lintJson(data.value);
      this.saveHistoryInfo();
      const {
        onChange
      } = this.props;

      if (onChange) {
        onChange(data);
      }
    };

    this.onUserSettingsUpdate = debounce(({
      settings
    }) => {
      this.props.refreshSettingsState(settings);
    }, 10);

    this.refCodemirror = ref => {
      const cm = ref && ref.codemirror;

      if (this.codemirror && this.codemirror === cm) {
        return;
      }

      this.codemirror = cm;

      if (this.props.refCodemirror) {
        this.props.refCodemirror(ref);
      }

      if (this.codemirror) this.codemirrorDidMount();
    };
  }

  componentDidMount() {
    this.restoreSession();
    this.respondToSetCursorFlash();
    this.lintJson(this.props.value);
    this.__compareProps = Object.keys(this.props);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.isCodeMirrorMounted !== nextState.isCodeMirrorMounted) return true;
    if (this.props === nextProps) return false;
    if (this.props.controlled) return true;
    if (this.props.sessionId !== nextProps.sessionId) return true; // Only update on flashes when last value was false and next is true.

    if (!this.props.triggerUndoFlash && nextProps.triggerUndoFlash) return true;
    if (!this.props.triggerRedoFlash && nextProps.triggerRedoFlash) return true; // Compare props

    if (this.__compareProps.length !== Object.keys(nextProps).length) return true;
    return this.__compareProps.some(key => {
      if (updateCompareIgnoreProps[key]) return false;
      return this.props[key] !== nextProps[key];
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sessionId !== this.props.sessionId) {
      this.restoreSession();
    }

    this.respondToSetCursorFlash();
    this.respondToUndoFlash();
    this.respondToRedoFlash();

    if (this.statusBarManager) {
      this.statusBarManager.updateProps(this.props);
    }

    if (!this.props.enableJsonLint && prevProps.enableJsonLint) {
      const {
        onJsonLint
      } = this.props;
      if (onJsonLint) onJsonLint({
        error: null
      });
    } else if (this.props.enableJsonLint && !prevProps.enableJsonLint || this.props.onJsonLint !== prevProps.onJsonLint) {
      if (this.codemirror) {
        this.lintJson(this.codemirror.getValue());
      }
    }

    this.__compareProps = Object.keys(this.props);
  }

  componentWillUnmount() {
    clearTimeout(this.refreshTimeout);
    if (this.statusBarManager) this.statusBarManager.destruct();
  }
  /**
   * Stores prop keys from last render to optimize shouldComponentUpdate test.
   */


  respondToUndoFlash() {
    const {
      triggerUndoFlash
    } = this.props;

    if (!(triggerUndoFlash && this.codemirror)) {
      return;
    }

    this.codemirror.doc.undo();
  }

  respondToRedoFlash() {
    const {
      triggerRedoFlash
    } = this.props;

    if (!(triggerRedoFlash && this.codemirror)) {
      return;
    }

    this.codemirror.doc.redo();
  }

  respondToSetCursorFlash() {
    const {
      setCursorFlash
    } = this.props;

    if (!(setCursorFlash && this.codemirror)) {
      return;
    }

    const {
      from,
      to
    } = setCursorFlash;
    this.codemirror.setSelection(to, from);
    this.codemirror.focus();
  }

  restoreSession() {
    this.props.restoreSession(this.codemirror);
  }
  /*
   * Events
   */


  updateEditorSession(sessionData) {
    if (this.props.disableSession) return;
    this.props.updateEditorSession(this.props.sessionId, sessionData);
  }

  lintJson(source) {
    if (!(this.props.enableJsonLint && this.props.onJsonLint && this.props.mode === 'application/json' && typeof source === 'string')) return;
    const error = validateJSON(source, this.props.sessionId);
    this.props.onJsonLint({
      error
    });
  }

  // Refreshes codemirror session in response to changes in width
  codemirrorRefresh() {
    if (!this.codemirror) return;
    const codemirrorWrapper = this.codemirror.getWrapperElement();
    const {
      height,
      width
    } = codemirrorWrapper.getBoundingClientRect();

    if (height !== this.codemirrorHeight || width !== this.codemirrorWidth) {
      this.codemirrorHeight = height;
      this.codemirrorWidth = width;
      refresh(this.codemirror);
    }
  }

  codemirrorDidMount() {
    this.setState({
      isCodeMirrorMounted: true
    }); // TODO: Better this responds to an event.

    const checkSize = () => {
      if (!this.codemirror) return;
      this.codemirrorRefresh();
      this.refreshTimeout = setTimeout(checkSize, 500);
    };

    checkSize();
    this.statusBarManager = new CodemirrorManager({
      codemirror: this.codemirror,
      props: this.props
    });
  }
  /*
   * Render
   */


  get className() {
    return classNames(this.props.className, 'ide-codemirror-wrapper', this.state.isCodeMirrorMounted && 'ide-codemirror--mounted');
  }

  getRenderProps() {
    const props = omitBlacklistedRenderProps(this.props);
    /*
     * BUG: `height: 'auto'` causes a significant performance problem
     * when unmounting editors with very long files - 10k+ lines.
     * See https://issues.hubspotcentral.com/browse/CG-11568
     */

    if (props.height) {
      // Test with regex b/c the following is valid
      //    div.style.height = "    AuTo    ";
      const autoRegex = /^\s*\bauto\b\s*$/i;

      if (autoRegex.test(props.height)) {
        props.height = undefined;
      }
    }

    return props;
  }

  render() {
    const props = this.getRenderProps();
    return /*#__PURE__*/_jsx(DecoratedCodeMirror, Object.assign({}, props, {
      className: this.className,
      refCodemirror: this.refCodemirror,
      onCursorActivity: this.onCursorActivity,
      onScroll: this.onScroll,
      onFold: this.onFold,
      onUnfold: this.onUnfold,
      onChange: this.onChange // @annotations
      ,
      disableAnnotationTextMarkers: false,
      disableAnnotationGutterMessages: false,
      disableAnnotationGutterNavigation: false,
      enableAnnotationScrollbarMarkers: true,
      enableStickyAnnotations: true,
      enableSanitizeAnnotationMessages: true // @find
      ,
      findPanelPosition: Position.TOP // @userSettings
      ,
      onUserSettingsFetchSuccess: this.onUserSettingsUpdate,
      onUserSettingsChange: this.onUserSettingsUpdate
    }));
  }

}

IDECodeMirror.propTypes = {
  /**
   * A unique identifier used to maintain editor session data
   * like cursor and scroll position.
   */
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  disableSession: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onCursorActivity: PropTypes.func,
  onScroll: PropTypes.func,
  onFold: PropTypes.func,
  onUnfold: PropTypes.func,
  refCodemirror: PropTypes.func,
  mode: PropTypes.string,
  value: PropTypes.string,
  enableJsonLint: PropTypes.bool,
  onJsonLint: PropTypes.func,
  // Used for split view to update codemirror when another session updates
  controlled: PropTypes.bool,
  // From connect()
  userId: PropTypes.number.isRequired,
  updateEditorSession: PropTypes.func.isRequired,
  refreshSettingsState: PropTypes.func.isRequired,
  setCursorFlash: PropTypes.shape({
    from: PropTypes.instanceOf(Pos),
    to: PropTypes.instanceOf(Pos)
  }),
  triggerUndoFlash: PropTypes.bool,
  triggerRedoFlash: PropTypes.bool,
  updateHistory: PropTypes.bool,
  codemirrorType: PropTypes.string,
  registerStatusBar: PropTypes.func,
  restoreSession: PropTypes.func.isRequired,
  enableEsLint: PropTypes.bool.isRequired,
  esLintConfig: PropTypes.object.isRequired
};
IDECodeMirror.defaultProps = {
  updateHistory: false,
  controlled: false
};
export default connect(makeMapStateToProps, mapDispatchToProps)(IDECodeMirror);